import UnlimitedInput from '@/components/UnlimitedInput'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import { UNLIMITED } from '@/modules/Contract/constants'
import {
    getServiceConfigoptions,
    updateServiceConfigoptions,
} from '@/modules/Service/services'
import {
    ServiceConfigoption,
    ServiceInformationProps,
} from '@/modules/Service/types'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Alert, Button, GridHookForm, IconButton, Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import PAYG from '../PAYG'
import viewSettingSchema from './schema'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import BackIcons from '@/icons/BackIcons'

interface ServiceInformationFormOptions {
    service_configoptions: Partial<
        ServiceConfigoption & {
            time_optional?: boolean
        }
    >[]
}

const ViewSetting: React.FC<ServiceInformationProps> = ({
    service,
    view_setting,
    peek_settings,
}) => {
    const [forceShowCommitmentUI, setForceShowCommitmentUI] = useState(false)
    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_CONFIGOPTIONS, service?.service_id],
        queryFn: () =>
            getServiceConfigoptions({
                serviceID: service?.service_id,
            }),
    })

    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const { mutateAsync: updateConfigOptions, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: updateServiceConfigoptions,
            },
            {
                successMessage: t('update-success'),
                reactQueryKeys: QUERY_KEYS.SERVICES,
                onSuccess: () => {
                    // Refresh query keys
                    queryClient.invalidateQueries({
                        queryKey: [
                            'services.configoptions',
                            service?.service_id,
                        ],
                    })
                    queryClient.invalidateQueries({
                        queryKey: [`services.${service?.service_id}`],
                    })
                },
            }
        )

    const formControlOptions = useForm<ServiceInformationFormOptions>({
        defaultValues: {
            service_configoptions: data?.map((i) => ({
                ...i,
                time_optional: i?.commitment_time && i?.commitment_time > 1,
            })) as any,
        },
        resolver: yupResolver(viewSettingSchema as any),
    })

    const handleChangeCommitmentPriceTotal = (total: number, index: number) => {
        formControlOptions.setValue(
            `service_configoptions.${index}.commitment_price_total` as any,
            total
        )
    }
    const { fields, remove } = useFieldArray({
        control: formControlOptions.control,
        name: 'service_configoptions',
    })

    const handleSave = async (values: ServiceInformationFormOptions) => {
        if (!service?.service_id) return

        try {
            await updateConfigOptions({
                serviceID: service.service_id,
                service_configoptions: values.service_configoptions?.map(
                    (i) => ({
                        ...i,
                        commitment_value: parseInt(
                            i?.commitment_value?.toString() || '0'
                        ),
                    })
                ),
            })
        } catch (error) {
            console.error('Error updating service config options:', error)
        }
    }

    useEffect(() => {
        if (!data) return
        formControlOptions?.reset({
            service_configoptions: data?.map((i) => ({
                ...i,
                name: i?.name,
                time_optional: i?.commitment_time && i?.commitment_time > 1,
            })) as any,
        })
    }, [data])

    const hasCommitment = useMemo(() => {
        return data?.some((i) => i?.commitment_value && i?.commitment_value > 0)
    }, [data])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent
    if (!data)
        return (
            <Alert variant="info">
                <Typogaphy>{t('no_data')}</Typogaphy>
            </Alert>
        )

    // PAYG UI - Beautiful and user-friendly design
    if (!hasCommitment && !forceShowCommitmentUI) {
        return (
            <PAYG
                data={data}
                onEnableFixedPackages={() => setForceShowCommitmentUI(true)}
            />
        )
    }

    // Standard commitment-based UI
    return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm mt-2">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg
                                className="w-4 h-4 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div>
                            <Typogaphy className="text-slate-900 font-medium">
                                {t('services.commitments.info')}
                            </Typogaphy>
                            <Typogaphy className="text-slate-500 text-sm">
                                {t('services.commitments.subtitle')}
                            </Typogaphy>
                        </div>
                    </div>
                    {!hasCommitment && forceShowCommitmentUI && (
                        <Button
                            variant="outlined"
                            color="info"
                            size="small"
                            onClick={() => setForceShowCommitmentUI(false)}
                            className="text-sm"
                            startIcon={<BackIcons />}
                        >
                            {t('services.payg.back')}
                        </Button>
                    )}
                </div>
            </div>
            <div className="p-4">
                <GridHookForm
                    actions={
                        view_setting
                            ? []
                            : [
                                  {
                                      type: 'submit',
                                      title: t('save'),
                                      variant: 'contained',
                                      loading: isUpdating,
                                      disabled:
                                          view_setting ||
                                          !formControlOptions?.formState
                                              .isDirty ||
                                          isUpdating,
                                  },
                              ]
                    }
                    formControl={formControlOptions}
                    data={[
                        {
                            className: 'col-span-4',
                            renderEditComponent: () => {
                                return (
                                    <div className="space-y-4">
                                        {fields?.map((field, index) => {
                                            return (
                                                <div
                                                    key={field.id}
                                                    className="bg-slate-50/50 border border-slate-200 rounded-lg p-4"
                                                >
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                                <Typogaphy className="text-blue-600 font-medium text-xs">
                                                                    {index + 1}
                                                                </Typogaphy>
                                                            </div>
                                                            <Typogaphy className="font-medium text-slate-800">
                                                                {t(
                                                                    [
                                                                        'global',
                                                                        'local',
                                                                    ].includes(
                                                                        field.name as string
                                                                    ) &&
                                                                        fields?.length ===
                                                                            1
                                                                        ? 'total-bandwidth'
                                                                        : (field.name as string)
                                                                )}
                                                            </Typogaphy>
                                                        </div>
                                                        <IconButton
                                                            size="small"
                                                            className="hover:bg-red-50 text-red-500 transition-colors"
                                                            onClick={() =>
                                                                remove(index)
                                                            }
                                                        >
                                                            <DeleteOutlineIcons />
                                                        </IconButton>
                                                    </div>

                                                    <GridHookForm
                                                        formControl={
                                                            formControlOptions
                                                        }
                                                        subForm
                                                        actions={[]}
                                                        className="grid-cols-3 gap-4"
                                                        data={[
                                                            {
                                                                renderEditComponent:
                                                                    () => (
                                                                        <UnlimitedInput
                                                                            value={
                                                                                field?.commitment_value
                                                                            }
                                                                            onChange={(
                                                                                value
                                                                            ) => {
                                                                                formControlOptions.setValue(
                                                                                    `service_configoptions.${index}.commitment_value` as any,
                                                                                    value
                                                                                )
                                                                                const commitment =
                                                                                    parseInt(
                                                                                        value ||
                                                                                            '0'
                                                                                    )
                                                                                let total = 0
                                                                                if (
                                                                                    commitment >=
                                                                                    UNLIMITED
                                                                                ) {
                                                                                    total =
                                                                                        formControlOptions.watch(
                                                                                            `service_configoptions.${index}.commitment_price_per_unit`
                                                                                        ) ||
                                                                                        0
                                                                                } else {
                                                                                    total =
                                                                                        parseInt(
                                                                                            value ||
                                                                                                '0'
                                                                                        ) *
                                                                                        (formControlOptions.watch(
                                                                                            `service_configoptions.${index}.commitment_price_per_unit`
                                                                                        ) ||
                                                                                            0)
                                                                                }
                                                                                handleChangeCommitmentPriceTotal(
                                                                                    total,
                                                                                    index as number
                                                                                )
                                                                            }}
                                                                            placeholder={t(
                                                                                'commitment_value'
                                                                            )}
                                                                            label={t(
                                                                                'commitment_value'
                                                                            )}
                                                                            className="bg-white"
                                                                        />
                                                                    ),
                                                            },
                                                            {
                                                                formItemProps: {
                                                                    name: `service_configoptions.${index}.commitment_price_per_unit`,
                                                                    type: 'number',
                                                                    label: t(
                                                                        'commitment_price_per_unit'
                                                                    ),
                                                                    size: 'small',
                                                                    disabled:
                                                                        (formControlOptions?.watch(
                                                                            `service_configoptions.${index}.commitment_value`
                                                                        ) ||
                                                                            0) <=
                                                                        0,
                                                                },
                                                                className:
                                                                    peek_settings?.includes(
                                                                        'commitment_price_per_unit'
                                                                    )
                                                                        ? 'bg-white rounded border border-slate-200'
                                                                        : 'hidden',
                                                            },
                                                            {
                                                                formItemProps: {
                                                                    name: `service_configoptions.${index}.commitment_price_total`,
                                                                    type: 'number',
                                                                    label: t(
                                                                        'commitment_price_total'
                                                                    ),
                                                                    size: 'small',
                                                                },
                                                                className:
                                                                    peek_settings?.includes(
                                                                        'commitment_price_total'
                                                                    )
                                                                        ? 'bg-white rounded border border-slate-200'
                                                                        : 'hidden',
                                                            },
                                                            {
                                                                formItemProps: {
                                                                    name: `service_configoptions.${index}.time_optional`,
                                                                    type: 'checkbox',
                                                                    defaultValue:
                                                                        field.time_optional,
                                                                    placeholder:
                                                                        t(
                                                                            'commits.time_optional'
                                                                        ),
                                                                    helperText:
                                                                        t(
                                                                            'commits.time_optional_helper'
                                                                        ),
                                                                },
                                                                className:
                                                                    peek_settings?.includes(
                                                                        'commitment_time'
                                                                    )
                                                                        ? ''
                                                                        : 'hidden',
                                                            },
                                                            {
                                                                formItemProps: {
                                                                    label: t(
                                                                        'commitment_time'
                                                                    ),
                                                                    name: `service_configoptions.${index}.commitment_time`,
                                                                    type: 'number',
                                                                    placeholder:
                                                                        t(
                                                                            'commits.commitment_time'
                                                                        ),
                                                                },
                                                                className:
                                                                    peek_settings?.includes(
                                                                        'commitment_time'
                                                                    ) &&
                                                                    formControlOptions.watch(
                                                                        `service_configoptions.${index}.time_optional`
                                                                    )
                                                                        ? ''
                                                                        : 'hidden',
                                                            },
                                                            {
                                                                formItemProps: {
                                                                    label: t(
                                                                        'commitment_available'
                                                                    ),
                                                                    name: `service_configoptions.${index}.commitment_available`,
                                                                    type: 'number',
                                                                    placeholder:
                                                                        t(
                                                                            'commitment_available'
                                                                        ),
                                                                    disabled: true,
                                                                },
                                                                className:
                                                                    peek_settings?.includes(
                                                                        'commitment_time'
                                                                    ) &&
                                                                    formControlOptions.watch(
                                                                        `service_configoptions.${index}.time_optional`
                                                                    )
                                                                        ? ''
                                                                        : 'hidden',
                                                            },
                                                            // Checkboxes
                                                            {
                                                                formItemProps: {
                                                                    name: `service_configoptions.${index}.commitment_prepaid`,
                                                                    type: 'checkbox',
                                                                    placeholder:
                                                                        t(
                                                                            'commits.commitment_prepaid'
                                                                        ),
                                                                },
                                                                className:
                                                                    peek_settings?.includes(
                                                                        'commitment_prepaid'
                                                                    )
                                                                        ? ''
                                                                        : 'hidden',
                                                            },
                                                            {
                                                                formItemProps: {
                                                                    name: `service_configoptions.${index}.statementable`,
                                                                    type: 'checkbox',
                                                                    placeholder:
                                                                        t(
                                                                            'contract.monthly_statement'
                                                                        ),
                                                                },
                                                                className:
                                                                    peek_settings?.includes(
                                                                        'statementable'
                                                                    )
                                                                        ? 'col-span-2'
                                                                        : 'hidden',
                                                            },
                                                        ]}
                                                    />
                                                </div>
                                            )
                                        })}
                                    </div>
                                )
                            },
                        },
                    ]}
                    onSubmit={handleSave}
                />
            </div>
        </div>
    )
}

export default ViewSetting
